/**
 * Company members — sv_organization, addressed by company_id in the PATH. Authorization
 * is the JWT plus the Casbin domain company:{id}; no tenant header, so request(), never
 * requestControl().
 *
 *   GET    /companies/{id}/members             → {meta, data}
 *   POST   /companies/{id}/members             → add, optionally WITH a role
 *   PUT    /companies/{id}/members/{member_id} → department / position / active
 *   DELETE /companies/{id}/members/{member_id} → remove access
 *
 * An earlier note here said adding a member could not grant a role, so the app offered
 * neither. That is not the case: `POST /companies/{id}/members` takes an optional
 * `role` from a closed set — a company with no way to appoint a second administrator
 * from the cabinet is a company whose owner cannot go on holiday.
 *
 * Changing a role AFTERWARDS is `/roles/*`, and it moved out of this file to
 * `entities/role/api.ts`. Not tidying: those routes take their Casbin domain from
 * `x-company-id`, and living here they were sent through the plain `request()` — no
 * header, so `system` domain, so 403 for every company owner who pressed the chip. A
 * call that must carry the tenant header does not belong in a module whose other five
 * calls must not.
 */
import {request} from '@/shared/api';
import type {Member, PagedMembers} from './types';

export type FetchMembersParams = {
  /**
   * Free text over the ENRICHED fields — full_name, email, username, phone, position.
   * Not over anything sv_organization stores itself, which is why a search for a
   * department name finds nothing.
   */
  search?: string;
  page?: number;
  perPage?: number;
  /** One department. The sv_organization id, the same one the picker chips carry. */
  departmentId?: string;
  /**
   * ⚠️ Forwarded as a plain STRING filter ("true"/"false"), not a JSON boolean — which
   * is why this is a boolean here and a string on the wire, converted once.
   */
  isActive?: boolean;
  /** Exact match on the member's position/title, not a substring. */
  position?: string;
  /** `full_name` | `email` | `username` | `phone` — sorted by the gateway itself. */
  sortBy?: string;
  sortOrder?: 'asc' | 'desc';
};

export function fetchMembers(
  companyId: string,
  params: FetchMembersParams = {},
): Promise<PagedMembers> {
  const qs = new URLSearchParams();
  if (params.search) {
    qs.set('search', params.search);
  }
  if (params.page !== undefined) {
    qs.set('page', String(params.page));
  }
  if (params.perPage !== undefined) {
    qs.set('per_page', String(params.perPage));
  }
  if (params.departmentId) {
    qs.set('department_id', params.departmentId);
  }
  if (params.isActive !== undefined) {
    qs.set('is_active', String(params.isActive));
  }
  if (params.position) {
    qs.set('position', params.position);
  }
  if (params.sortBy) {
    qs.set('sort_by', params.sortBy);
  }
  if (params.sortOrder) {
    qs.set('sort_order', params.sortOrder);
  }
  const query = qs.toString();
  return request<PagedMembers>(
    query
      ? `/companies/${encodeURIComponent(companyId)}/members?${query}`
      : `/companies/${encodeURIComponent(companyId)}/members`,
  );
}

export function removeMember(
  companyId: string,
  memberId: string,
): Promise<void> {
  return request<void>(
    `/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`,
    {method: 'DELETE'},
  );
}

/** The closed set of company-scoped roles `POST /members` will grant at add time. */
export type CompanyRole = 'owner' | 'admin' | 'accountant';

/**
 * Add a member — either an account that already exists, or one created inline.
 *
 * Exactly one of `accountId` / `newMember` must be given: naming neither is a 400
 * ("Provide member_account_id or new_member").
 *
 * ⚠️ `role` is granted BEST-EFFORT, after the membership row is written. A failure at
 * that second step is logged server-side and not returned, so a member can come back
 * added-but-roleless. Callers that asked for a role should re-read the list and check
 * `roles` rather than assume; {@link assignRole} fixes it without re-adding anyone.
 */
export function addMember(
  companyId: string,
  input: {
    accountId?: string;
    newMember?: {
      email?: string;
      phone?: string;
      username?: string;
      full_name?: string;
      /** Required when creating the account inline. */
      password: string;
    };
    departmentId?: string;
    position?: string;
    role?: CompanyRole;
  },
): Promise<Member> {
  const body: Record<string, unknown> = {};
  if (input.accountId) {
    body.member_account_id = input.accountId;
  }
  if (input.newMember) {
    body.new_member = input.newMember;
  }
  if (input.departmentId) {
    body.department_id = input.departmentId;
  }
  if (input.position !== undefined) {
    body.position = input.position;
  }
  if (input.role) {
    body.role = input.role;
  }
  return request<Member>(`/companies/${encodeURIComponent(companyId)}/members`, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

/**
 * Update a membership.
 *
 * ⚠️ `isActive` is REQUIRED here even when the caller only means to change a position.
 * `UpdateMemberRequest.is_active` is a plain bool on the wire, so the gateway cannot
 * tell "not sent" from "false" and defaults a missing value to **true** — omitting it
 * silently reactivates a member somebody deliberately deactivated. Making the parameter
 * non-optional in this signature is how that trap is closed for every caller at once:
 * pass the member's current value back unless you are changing it.
 */
export function updateMember(
  companyId: string,
  memberId: string,
  update: {isActive: boolean; departmentId?: string; position?: string},
): Promise<Member> {
  return request<Member>(
    `/companies/${encodeURIComponent(companyId)}/members/${encodeURIComponent(memberId)}`,
    {
      method: 'PUT',
      body: JSON.stringify({
        is_active: update.isActive,
        department_id: update.departmentId ?? '',
        position: update.position ?? '',
      }),
    },
  );
}
